import React from 'react';
import { MapPin, Star, CheckCircle, Heart } from 'lucide-react';
import { ProviderSearchResult } from '../types';
import { Badge } from './Badge';
import { Button } from './Button';

interface ProviderCardProps {
  provider: ProviderSearchResult;
  onViewDetails?: (providerId: string) => void;
  onFavorite?: (providerId: string) => void;
  isFavorite?: boolean;
}

export const ProviderCard: React.FC<ProviderCardProps> = ({
  provider,
  onViewDetails,
  onFavorite,
  isFavorite = false,
}) => {
  if (provider.isBlocked) {
    console.warn('Blocked provider card attempted to render:', provider.id);
    return null;
  }

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(provider.id);
    }
  };

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFavorite) {
      onFavorite(provider.id);
    }
  };

  const getDistanceColor = (distance?: number) => {
    if (!distance) return 'text-gray-600';
    if (distance <= 5) return 'text-green-600';
    if (distance <= 10) return 'text-blue-600';
    if (distance <= 20) return 'text-orange-600';
    return 'text-gray-600';
  };

  const getCareTypesArray = () => {
    if (Array.isArray(provider.typeOfCare)) {
      return provider.typeOfCare;
    }
    if (provider.typeOfCare && typeof provider.typeOfCare === 'string') {
      return [provider.typeOfCare];
    }
    if (Array.isArray(provider.careTypes)) {
      return provider.careTypes;
    }
    return [];
  };

  const careTypes = getCareTypesArray();
  const displayName = provider.businessName || provider.name;
  const startingPrice = provider.pricing?.hourly || provider.startingPrice;

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-semibold text-gray-900">{displayName}</h3>
              {provider.verified && (
                <CheckCircle className="w-5 h-5 text-blue-600" />
              )}
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-600">
              {provider.distanceMiles !== undefined && (
                <div className={`flex items-center gap-1 ${getDistanceColor(provider.distanceMiles)}`}>
                  <MapPin className="w-4 h-4" />
                  <span className="font-medium">{provider.distanceMiles.toFixed(1)} miles</span>
                </div>
              )}

              {provider.cqcRating && (
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="font-medium">{provider.cqcRating.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleFavorite}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart
              className={`w-5 h-5 ${
                isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'
              }`}
            />
          </button>
        </div>

        {provider.location && (
          <p className="text-sm text-gray-600 mb-3">{provider.location}</p>
        )}

        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-2">Services Offered:</p>
          <div className="flex flex-wrap gap-2">
            {careTypes.slice(0, 3).map((type, index) => (
              <Badge key={index} variant="primary">
                {type}
              </Badge>
            ))}
            {careTypes.length > 3 && (
              <Badge variant="info">+{careTypes.length - 3} more</Badge>
            )}
          </div>
        </div>

        {provider.rankScore !== undefined && provider.rankScore > 0 && (
          <div className="mb-3">
            <Badge variant="success">
              Match Score: {provider.rankScore}
            </Badge>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          {startingPrice && (
            <div>
              <p className="text-sm text-gray-500">Starting from</p>
              <p className="text-2xl font-bold text-gray-900">£{startingPrice}</p>
              <p className="text-xs text-gray-500">per hour</p>
            </div>
          )}

          <Button onClick={handleViewDetails}>
            View Details
          </Button>
        </div>
      </div>
    </div>
  );
};
